import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@shared/api/client';
import { queryKeys } from '@shared/api/queryKeys';

/** Ответ `GET /api/v1/config` — публичная конфигурация и лимиты MVP. */
export interface PublicConfig {
  app_name: string;
  environment: string;
  default_locale: string;
  supported_locales: string[];
  currency: string;
  country_code: string;
  new_listing_days: number;
  llm_enabled: boolean;
  limits: {
    chat_message_max_length: number;
    hero_prompt_min_length: number;
    hero_prompt_max_length: number;
    catalog_page_size: number;
    /** Размер рабочего набора и четвёрки (Epic 22): числа статистики — из API. */
    shortlist_max: number;
    top_selection_size: number;
  };
  /**
   * Оплата (BE-210). Цена приходит **только отсюда**: хардкод цены в интерфейсе
   * и в текстах локалей запрещён (MVP_TECH_RFC §25).
   */
  payments: {
    enabled: boolean;
    price_cents: number;
    currency: string;
    /**
     * `stub` — демонстрационный режим, и экран обязан сказать об этом прямо;
     * `stripe` — настоящая платёжная форма.
     */
    provider: string;
    /**
     * **Публикуемый** ключ платёжной системы: он для того и существует, чтобы
     * уходить в браузер. Секретный ключ на фронт не попадает никогда и в этом
     * типе не представлен.
     */
    publishable_key: string;
    /** `true` — списываются настоящие деньги, а не тестовые. */
    live_mode: boolean;
    /** `manual` — деньги замораживаются на карте до отдельного списания. */
    capture_method: string;
    vat_rate_bps: number;
    vat_country: string;
  };
  /**
   * Условия вознаграждения по результату (MVP_PRD §42). Ставка и сумма — числа,
   * а не текст: иначе «25 %» в локали и `SUCCESS_FEE_RATE_BPS` разойдутся молча.
   */
  success_fee: {
    enabled: boolean;
    rate_bps: number;
    flat_cents: number;
  };
  /**
   * Гостевой контур (FE-205). Флаг нужен, чтобы **не предлагать** путь без
   * регистрации там, где его нет: при выключенном контуре гостевые endpoint'ы
   * отвечают `404`, и кнопка «начать без регистрации» вела бы в никуда.
   */
  guest: {
    enabled: boolean;
    max_messages: number;
    session_ttl_days: number;
  };
  auth: {
    otp_code_length: number;
    otp_ttl_seconds: number;
    otp_resend_after_seconds: number;
    otp_max_attempts: number;
  };
  /**
   * Переговорный контур (Epic 18, FE-180). `dry_run` показывается явно:
   * «переговоры идут» и «письма не отправляются» перепутать нельзя (§25).
   * Блок опционален по той же причине, что и `legal`: откат backend'а на
   * версию без него не должен ронять интерфейс.
   */
  negotiations?: {
    enabled: boolean;
    dry_run: boolean;
    autonomy_enabled: boolean;
  };
  legal: LegalConfig;
}

/** Реквизиты оператора — § 5 DDG и Art. 246a §1 Abs. 1 S. 1 Nr. 2–3 EGBGB. */
export interface OperatorConfig {
  legal_name: string;
  street: string;
  postal_code: string;
  city: string;
  country: string;
  managing_director: string;
  register_name: string;
  register_authority: string;
  registration_number: string;
  vat_id: string;
  email: string;
  /** Обязателен в преддоговорной информации (Art. 246a §1 Abs. 1 S. 1 Nr. 3). */
  phone: string;
  website: string;
}

/** Раздел Widerrufsbelehrung: заголовок и абзацы. */
export interface WithdrawalSection {
  title: string;
  paragraphs: string[];
}

/**
 * Правовой контур (BE, `app/core/legal.py`).
 *
 * **Реквизиты и Widerrufsbelehrung приходят с сервера, а не лежат в локалях.**
 * Тот же текст дословно уходит в подтверждение договора (§ 312f Abs. 2 BGB), и
 * две копии — на странице и в письме — разошлись бы при первой правке.
 * Расхождение здесь стоит продления срока отзыва до 12 месяцев и 14 дней.
 */
export interface LegalConfig {
  operator: OperatorConfig;
  /** Текст согласия на досрочное начало услуги (§ 356 Abs. 5 Nr. 2 BGB). */
  immediate_start_consent: { version: string; text: string };
  document_versions: Record<string, string>;
  withdrawal_policy: WithdrawalSection[];
  withdrawal_form_lines: string[];
  withdrawal_period_days: number;
  withdrawal_refund_days: number;
  /** Показывать ли «Erklärung zur Barrierefreiheit» (BFSG). */
  accessibility_statement_enabled: boolean;
  /**
   * Реально подключённые необязательные категории cookie. Пустой список —
   * баннера согласия нет вовсе: строго необходимое согласия не требует
   * (§ 25 Abs. 2 TDDDG), а баннер «ни о чём» обесценивает согласие там, где
   * оно однажды понадобится.
   */
  optional_cookie_categories: string[];
}

/** Значения по умолчанию: интерфейс остаётся рабочим, пока API недоступен. */
export const FALLBACK_LIMITS: PublicConfig['limits'] = {
  chat_message_max_length: 2000,
  hero_prompt_min_length: 10,
  hero_prompt_max_length: 2000,
  catalog_page_size: 12,
  shortlist_max: 100,
  top_selection_size: 4,
};

/**
 * Правовой блок конфигурации, прочитанный защитно.
 *
 * Остальные поля читаются напрямую (`data?.limits.shortlist_max`), и это
 * правильно: экран без своих данных всё равно нерабочий. Здесь иначе —
 * провайдер согласия и подвал стоят **над всем приложением**, и обращение к
 * отсутствующему блоку уронило бы не экран, а весь интерфейс: откат backend'а
 * на версию без `legal` не должен превращаться в белую страницу.
 *
 * Отсутствие блока означает «правовых настроек нет»: баннер согласия не
 * показывается, заявление о доступности не предлагается. Это корректная
 * деградация — в обоих случаях мы просто ничего не обещаем.
 */
export function legalConfig(config: PublicConfig | undefined): LegalConfig | undefined {
  const legal: LegalConfig | undefined = config?.legal;
  return legal;
}

export function useConfig() {
  return useQuery({
    queryKey: queryKeys.config(),
    queryFn: () => apiFetch<PublicConfig>('/config'),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}
